import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { addMonths, addWeeks, format } from 'date-fns';
import {
  Check,
  X,
  ExternalLink,
  ShieldCheck,
  CalendarClock,
  PhoneCall,
  MessageSquare,
  MessageCircle,
  CheckCircle2,
  Ban,
} from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useUIStore } from '@/state/uiStore';
import { resolveNoDepositOverride } from '@/domain/approvals';
import { flagNoDepositDecline } from '@/domain/noDepositDeclineFlow';
import { ensureConfirmationCall, resolveConfirmationOutcome } from '@/domain/confirmationFlow';
import { askArtistToConfirm } from '@/domain/confirmationEscalationFlow';
import { opsCancelBooking } from '@/domain/opsCancelFlow';
import { opsExtendPayBy } from '@/domain/opsExtendFlow';
import { resolveNoRebookFlag } from '@/domain/resolveNoRebookFlow';
import { canMarkConfirmed } from '@/domain/permissions';
import { selfPersonIdFromSession } from '@/utils/commsHelpers';
import { aud, customerName, simDateShort, simTimeFull, studioDisplay } from '@/utils/format';
import OpsBookingSummary from '@/components/ops/OpsBookingSummary';
import type { OpsItem } from '@/domain/opsWorklist';
import type { Booking, CustomerRecord, NoRebookFlag, StudioId } from '@/domain/types';

interface Props {
  item: OpsItem;
  onClose: () => void;
}

// The one Ops action modal (vault Diagram 09). Opened from a worklist row; each
// kind renders its own decision node - the layout language mirrors the
// ConfirmationWorkModal (overlay + panel + header + typed-reason gates). Ops-only
// actions (override approve/decline, cancel) are reached only from /ops, which is
// georgina-only. Light theme tokens throughout.

export default function OpsWorkModal({ item, onClose }: Props) {
  const navigate = useNavigate();
  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);
  const sessionArtistId = useSessionStore((s) => s.artistId);
  const apprenticeId = useSessionStore((s) => s.apprenticeId);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const pushNotification = useUIStore((s) => s.pushNotification);

  const [reason, setReason] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [messageSent, setMessageSent] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // `by` is the operator's display id on the domain stamps; `opsPersonId` is the
  // comms person who authors DMs (a null/system persona is treated as Ops).
  const by = persona ?? 'georgina';
  const opsPersonId =
    selfPersonIdFromSession({ persona, agentId, artistId: sessionArtistId, apprenticeId }) ?? by;

  const nameFor = (customerId: string): string => {
    const c: CustomerRecord | undefined = customers[customerId];
    return c ? customerName(c) : customerId.slice(-6);
  };

  const notify = (title: string, body?: string, intensity: 'high' | 'info' = 'info') =>
    pushNotification({ intensity, title, body, ttlMs: 4000 });

  const act = <T,>(fn: () => T): void => {
    fn();
    onClose();
  };

  const { title, subtitle, customerId } = headerFor(item, nameFor);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-cream/10 backdrop-blur-sm"
      onClick={onClose}
      data-testid="ops-work-modal"
    >
      <motion.div
        initial={{ scale: 0.98, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.98, opacity: 0 }}
        className="relative panel bg-ink-deep max-w-[1000px] w-full max-h-[88vh] rounded-xl flex flex-col overflow-hidden shadow-gold-glow"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-4 border-b border-gold-dim/20 px-6 py-4">
          <div className="min-w-0">
            <p className="label-small-caps text-gold-bright">{sectionLabel(item)}</p>
            <h2 className="heading-display text-2xl text-cream truncate mt-1">{title}</h2>
            <p className="font-serif text-sm text-cream-soft mt-0.5">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {customerId ? (
              <button
                type="button"
                onClick={() => navigate(`/customer/${customerId}`)}
                className="btn-quiet text-xs"
                data-testid="owm-open-record"
              >
                <ExternalLink size={12} /> Open record
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="btn-quiet"
              aria-label="Close"
              data-testid="owm-close"
            >
              <X size={14} />
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 min-h-0 overflow-y-auto">
          {/* LEFT: the booking + customer context behind the decision (Diagram 09) */}
          <aside className="md:border-r border-gold-dim/20 px-6 py-5">
            <p className="label-small-caps text-cream-faint mb-3">{leftHeader(item)}</p>
            <LeftContext item={item} bookings={bookings} />
          </aside>

          {/* RIGHT: the decision itself */}
          <div className="px-6 py-5 flex flex-col gap-4 min-w-0">
            <p className="label-small-caps text-cream-faint">{rightHeader(item)}</p>
            {item.kind === 'override' ? (
              <OverrideBody
                note={item.request.note}
                reason={reason}
                setReason={setReason}
                onApprove={() =>
                  act(() => {
                    resolveNoDepositOverride({
                      requestId: item.request.id,
                      approve: true,
                      resolvedBy: by,
                    });
                    notify(
                      'No-deposit booking approved',
                      'Confirmed, paying in full on the day.',
                      'high',
                    );
                  })
                }
                onDecline={() => {
                  const trimmed = reason.trim();
                  if (!trimmed) return;
                  act(() => {
                    resolveNoDepositOverride({
                      requestId: item.request.id,
                      approve: false,
                      resolvedBy: by,
                      resolutionNote: trimmed,
                    });
                    if (item.booking) {
                      flagNoDepositDecline({
                        customerId: item.booking.customerId,
                        sourceBookingId: item.booking.id,
                        reason: trimmed,
                        by,
                      });
                    }
                    notify(
                      'No-deposit request declined',
                      'Flagged - the Agent must now collect a deposit.',
                    );
                  });
                }}
              />
            ) : null}

            {item.kind === 'confirmation' ? (
              <ConfirmationBody
                artistAsked={item.artistAsked}
                canConfirm={persona === null || canMarkConfirmed(persona)}
                reason={reason}
                setReason={setReason}
                onAskArtist={() =>
                  act(() => {
                    if (!opsPersonId) return;
                    askArtistToConfirm(item.booking.id, { byPersonId: opsPersonId, at: clockIso });
                    notify(
                      'Asked the artist',
                      'Asked them to reach out from their IG with a deadline.',
                    );
                  })
                }
                onMarkConfirmed={() =>
                  act(() => {
                    const callId = ensureConfirmationCall(item.booking.id, { at: clockIso });
                    if (!callId) return;
                    resolveConfirmationOutcome(callId, 'confirmed', { by, at: clockIso });
                    notify('Marked confirmed', 'The booking is locked in.', 'high');
                  })
                }
                onCancel={() => {
                  if (!reason.trim()) return;
                  act(() => {
                    const callId = ensureConfirmationCall(item.booking.id, { at: clockIso });
                    if (!callId) return;
                    resolveConfirmationOutcome(callId, 'cancelled', { by, at: clockIso });
                    notify('Cancelled', 'Slot freed to the Sweeper.');
                  });
                }}
              />
            ) : null}

            {item.kind === 'topup-extension' || item.kind === 'payby-extension' ? (
              <ExtendBody
                payByIso={item.payByIso}
                clockIso={clockIso}
                reason={reason}
                setReason={setReason}
                canCancel={!!item.plan.bookingId}
                onExtend={(newDueIso) =>
                  act(() => {
                    const res = opsExtendPayBy({ planId: item.plan.id, newDueIso, by });
                    if (res.ok) notify('Pay-by extended', 'Reminder re-pointed to the new date.');
                  })
                }
                onCancel={() => {
                  const bookingId = item.plan.bookingId;
                  const trimmed = reason.trim();
                  if (!bookingId || !trimmed) return;
                  act(() => {
                    opsCancelBooking({ bookingId, reason: trimmed, by });
                    notify('Cancelled', 'Slot freed to the Sweeper.');
                  });
                }}
              />
            ) : null}

            {item.kind === 'unpaid-artist' ? (
              <UnpaidArtistBody
                studioId={item.studioId}
                artistName={item.artistName}
                amountAud={item.amountAud}
                dayIso={item.dayIso}
                draft={messageDraft}
                setDraft={setMessageDraft}
                sent={messageSent}
                onSend={(managerId, body) => {
                  const at = clockIso;
                  const threadId = useBrainStore
                    .getState()
                    .openOrCreateDm([opsPersonId, managerId], opsPersonId, at);
                  useBrainStore.getState().sendDmMessage(threadId, body, {
                    authorId: opsPersonId,
                    timestamp: at,
                    mentions: [managerId],
                  });
                  setMessageSent(true);
                  notify('Message sent', 'The studio manager will reply in the thread.');
                }}
              />
            ) : null}

            {item.kind === 'no-rebook' ? (
              <NoRebookBody
                flag={item.flag}
                onOpenThread={() => {
                  navigate(`/customer/${item.flag.customerId}`);
                  onClose();
                }}
                onMarkComplete={() =>
                  act(() => {
                    resolveNoRebookFlag(item.flag.id, { by });
                    notify(
                      'No-rebook flag cleared',
                      'Marked complete - the Sweeper keeps re-calling.',
                      'high',
                    );
                  })
                }
              />
            ) : null}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// --- header helpers ---------------------------------------------------------

function sectionLabel(item: OpsItem): string {
  switch (item.kind) {
    case 'override':
      return 'No-deposit override';
    case 'confirmation':
      return 'Confirmation escalated';
    case 'topup-extension':
      return 'Inside-7d top-up extension';
    case 'payby-extension':
      return 'Pay-by extension';
    case 'unpaid-artist':
      return 'Unpaid artist';
    case 'no-rebook':
      return 'No rebook · mid piece';
    default:
      return 'Ops';
  }
}

function headerFor(
  item: OpsItem,
  nameFor: (id: string) => string,
): { title: string; subtitle: string; customerId?: string } {
  switch (item.kind) {
    case 'override':
      return {
        title: item.booking ? nameFor(item.booking.customerId) : 'No-deposit request',
        subtitle: `Requested by ${item.request.requestedBy}${
          item.booking ? ` · ${studioDisplay(item.booking.studioId)}` : ''
        }`,
        customerId: item.booking?.customerId,
      };
    case 'confirmation':
      return {
        title: nameFor(item.booking.customerId),
        subtitle: `${studioDisplay(item.booking.studioId)} · ${simTimeFull(item.booking.start)}`,
        customerId: item.booking.customerId,
      };
    case 'topup-extension':
      return {
        title: nameFor(item.plan.customerId),
        subtitle: `${studioDisplay(item.plan.studioId)} · inside 7 days`,
        customerId: item.plan.customerId,
      };
    case 'payby-extension':
      return {
        title: nameFor(item.plan.customerId),
        subtitle: `${studioDisplay(item.plan.studioId)} · pay-by ${simDateShort(item.payByIso)}`,
        customerId: item.plan.customerId,
      };
    case 'unpaid-artist':
      return {
        title: item.artistName,
        subtitle: `${studioDisplay(item.studioId)} · ${aud(item.amountAud)} owed for ${simDateShort(item.dayIso)}`,
      };
    case 'no-rebook':
      return {
        title: nameFor(item.flag.customerId),
        subtitle: `${studioDisplay(item.flag.studioId)} · mid-piece, no rebook booked`,
        customerId: item.flag.customerId,
      };
    default:
      return { title: 'Ops item', subtitle: '' };
  }
}

// --- layout: the context column beside every decision -----------------------

function leftHeader(item: OpsItem): string {
  switch (item.kind) {
    case 'unpaid-artist':
      return 'The payout';
    case 'no-rebook':
      return 'The piece & customer';
    default:
      return 'The booking & customer';
  }
}

function rightHeader(item: OpsItem): string {
  switch (item.kind) {
    case 'unpaid-artist':
      return 'The message';
    case 'no-rebook':
      return 'Reason & action';
    default:
      return 'Your call';
  }
}

function LeftContext({ item, bookings }: { item: OpsItem; bookings: Record<string, Booking> }) {
  switch (item.kind) {
    case 'override':
      return <OpsBookingSummary customerId={item.booking?.customerId} booking={item.booking} />;
    case 'confirmation':
      return <OpsBookingSummary customerId={item.booking.customerId} booking={item.booking} />;
    case 'topup-extension':
      return <OpsBookingSummary customerId={item.plan.customerId} booking={item.booking} />;
    case 'payby-extension':
      return <OpsBookingSummary customerId={item.plan.customerId} />;
    case 'unpaid-artist':
      return (
        <PayoutSummary
          studioId={item.studioId}
          artistName={item.artistName}
          amountAud={item.amountAud}
          dayIso={item.dayIso}
        />
      );
    case 'no-rebook':
      return (
        <OpsBookingSummary
          customerId={item.flag.customerId}
          booking={bookings[item.flag.sourceBookingId]}
          pieceId={item.flag.pieceId}
          whenLabel="Last sat"
        />
      );
    default:
      return null;
  }
}

function PayoutSummary({
  studioId,
  artistName,
  amountAud,
  dayIso,
}: {
  studioId: StudioId;
  artistName: string;
  amountAud: number;
  dayIso: string;
}) {
  return (
    <div className="panel-dark p-3.5 grid grid-cols-2 gap-x-4 gap-y-3" data-testid="owm-summary">
      <SummaryField k="Artist" v={artistName} />
      <SummaryField k="Studio" v={studioDisplay(studioId)} />
      <SummaryField k="Owed" v={aud(amountAud)} stake />
      <SummaryField k="For" v={simDateShort(dayIso)} />
    </div>
  );
}

function SummaryField({ k, v, stake }: { k: string; v: string; stake?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="label-small-caps">{k}</p>
      <p
        className={clsx(
          'font-serif text-sm mt-0.5',
          stake ? 'text-blood font-semibold' : 'text-cream',
        )}
      >
        {v}
      </p>
    </div>
  );
}

function NoRebookBody({
  flag,
  onOpenThread,
  onMarkComplete,
}: {
  flag: NoRebookFlag;
  onOpenThread: () => void;
  onMarkComplete: () => void;
}) {
  const agents = useBrainStore((s) => s.agents);
  // Who ran checkout is not stored on the frozen flag, so we show the studio's
  // floor lead as a stand-in (real checkedOutBy attribution is a fast-follow).
  const floor = useMemo(
    () =>
      Object.values(agents).find(
        (a) =>
          a.role === 'studio-manager' &&
          (a.primaryStudio === flag.studioId || a.assignedStudios.includes(flag.studioId)),
      ) ?? Object.values(agents).find((a) => a.role === 'studio-manager'),
    [agents, flag.studioId],
  );
  const floorName = floor ? `${floor.firstName} ${floor.lastName}`.trim() : 'the floor';

  return (
    <>
      <div className="panel-dark p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="label-small-caps">Why no rebook</p>
          <span className="rounded-full border border-gold-dim/40 px-2 py-0.5 text-[10px] font-inter uppercase tracking-wider text-cream-faint">
            from checkout
          </span>
        </div>
        <p
          className="font-serif text-sm text-cream italic mt-1.5"
          data-testid="owm-norebook-reason"
        >
          &ldquo;{flag.reason}&rdquo;
        </p>
        <p className="font-mono text-[11px] text-cream-faint mt-2">
          Recorded at checkout by {floorName} · {simDateShort(flag.createdAt)}
        </p>
      </div>

      <div className="rounded-md border border-gold-dim/25 bg-ink-black/30 p-3">
        <p className="label-small-caps text-gold-bright">Where it routes</p>
        <ul className="mt-2 space-y-1.5 font-serif text-sm text-cream">
          <li className="flex items-start gap-2">
            <span className="h-2 w-2 rounded-full bg-gold-bright shrink-0 mt-1.5" />
            <span>
              <span className="font-semibold">Ops flag</span> - this item, for accountability
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="h-2 w-2 rounded-full bg-azure shrink-0 mt-1.5" />
            <span>
              <span className="font-semibold">Sweeper</span> - re-calling for gap-fills ·
              recent-tattoo flag
            </span>
          </li>
        </ul>
      </div>

      <p className="font-serif text-xs text-cream-soft">
        No rebook link needed - the Sweeper already re-calls. Mark complete once the follow-up is
        handled.
      </p>

      <div className="flex items-center justify-end gap-2 mt-auto">
        <button
          type="button"
          onClick={onOpenThread}
          className="inline-flex items-center gap-1.5 rounded-md border border-gold-dim/40 px-3 py-1.5 text-xs text-cream-soft hover:border-gold-bright/50 transition"
          data-testid="owm-open-thread"
        >
          <MessageCircle size={13} /> Open thread
        </button>
        <button
          type="button"
          onClick={onMarkComplete}
          className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition"
          data-testid="owm-mark-complete"
        >
          <CheckCircle2 size={13} /> Mark complete
        </button>
      </div>
    </>
  );
}

// --- per-kind bodies --------------------------------------------------------

function OverrideBody({
  note,
  reason,
  setReason,
  onApprove,
  onDecline,
}: {
  note: string;
  reason: string;
  setReason: (v: string) => void;
  onApprove: () => void;
  onDecline: () => void;
}) {
  return (
    <>
      <div className="panel-dark p-3">
        <p className="label-small-caps">Their reason</p>
        <p className="font-serif text-sm text-cream mt-1">&ldquo;{note}&rdquo;</p>
      </div>
      <p className="font-serif text-xs text-cream-soft">
        Approve to confirm the booking with the deposit waived, or decline (with a reason) so the
        Agent must chase a deposit - no second trip through Ops.
      </p>
      <label className="block">
        <span className="label-small-caps">Decline reason</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. new customer - take a $200 deposit link instead."
          className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-blood resize-none"
          data-testid="owm-decline-reason"
        />
      </label>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onDecline}
          disabled={!reason.trim()}
          className="inline-flex items-center gap-1.5 rounded-md border border-blood/50 bg-blood/10 px-3 py-1.5 text-xs text-blood hover:bg-blood/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="owm-decline"
        >
          <X size={13} /> Decline
        </button>
        <button
          type="button"
          onClick={onApprove}
          className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition"
          data-testid="owm-approve"
        >
          <ShieldCheck size={13} /> Approve
        </button>
      </div>
    </>
  );
}

function ConfirmationBody({
  artistAsked,
  canConfirm,
  reason,
  setReason,
  onAskArtist,
  onMarkConfirmed,
  onCancel,
}: {
  artistAsked: boolean;
  canConfirm: boolean;
  reason: string;
  setReason: (v: string) => void;
  onAskArtist: () => void;
  onMarkConfirmed: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <p className="font-serif text-xs text-cream-soft">
        Three sequences unanswered. Ask the artist to reach out from their IG with a deadline, mark
        it confirmed once they report back, or cancel.
      </p>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onAskArtist}
          className="inline-flex items-center justify-center gap-1.5 rounded-md border border-azure/50 bg-azure/10 px-3 py-2 text-sm text-azure hover:bg-azure/20 transition"
          data-testid="owm-ask-artist"
        >
          <PhoneCall size={14} />{' '}
          {artistAsked ? 'Ask the artist again' : 'Ask the artist to reach out'}
        </button>
        {canConfirm ? (
          <button
            type="button"
            onClick={onMarkConfirmed}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-2 text-sm text-mint hover:bg-mint/20 transition"
            data-testid="owm-mark-confirmed"
          >
            <Check size={14} /> Mark confirmed
          </button>
        ) : null}
      </div>
      <label className="block border-t border-gold-dim/20 pt-3">
        <span className="label-small-caps">Cancel reason</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. no response by the deadline."
          className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-blood resize-none"
          data-testid="owm-cancel-reason"
        />
      </label>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={!reason.trim()}
          className="inline-flex items-center gap-1.5 rounded-md border border-blood/50 bg-blood/10 px-3 py-1.5 text-xs text-blood hover:bg-blood/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="owm-cancel"
        >
          <Ban size={13} /> Cancel booking
        </button>
      </div>
    </>
  );
}

interface DatePreset {
  key: string;
  label: string;
  iso: string;
}

function ExtendBody({
  payByIso,
  clockIso,
  reason,
  setReason,
  canCancel,
  onExtend,
  onCancel,
}: {
  payByIso?: string;
  clockIso: string;
  reason: string;
  setReason: (v: string) => void;
  canCancel: boolean;
  onExtend: (newDueIso: string) => void;
  onCancel: () => void;
}) {
  // Push OUT from the later of now and the current pay-by, so every preset is a
  // genuine future extension even when the current date has already lapsed.
  const base = useMemo(() => {
    const now = new Date(clockIso);
    const due = payByIso ? new Date(payByIso) : now;
    return due.getTime() > now.getTime() ? due : now;
  }, [clockIso, payByIso]);

  const presets: DatePreset[] = useMemo(
    () => [
      { key: '1w', label: '+1 week', iso: addWeeks(base, 1).toISOString() },
      { key: '2w', label: '+2 weeks', iso: addWeeks(base, 2).toISOString() },
      { key: '1m', label: '+1 month', iso: addMonths(base, 1).toISOString() },
    ],
    [base],
  );
  const [chosen, setChosen] = useState<string>(presets[1]!.key);
  const chosenIso = presets.find((p) => p.key === chosen)?.iso ?? presets[1]!.iso;

  return (
    <>
      <p className="font-serif text-xs text-cream-soft">
        Extend the pay-by date. The reminder re-points to the new date and the cadence continues
        from there. Or cancel if it will not happen.
      </p>
      <div>
        <span className="label-small-caps">New pay-by date</span>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setChosen(p.key)}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs font-inter transition',
                p.key === chosen
                  ? 'border-gold-bright/60 text-gold-bright bg-gold-bright/10'
                  : 'border-gold-dim/40 text-cream-soft hover:border-gold-bright/50',
              )}
              data-testid={`owm-extend-date-${p.key}`}
            >
              {p.label} · {format(new Date(p.iso), 'EEE d MMM')}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => onExtend(chosenIso)}
          className="inline-flex items-center gap-1.5 rounded-md border border-mint/50 bg-mint/10 px-3 py-1.5 text-xs text-mint hover:bg-mint/20 transition"
          data-testid="owm-extend"
        >
          <CalendarClock size={13} /> Extend pay-by
        </button>
      </div>
      {canCancel ? (
        <>
          <label className="block border-t border-gold-dim/20 pt-3">
            <span className="label-small-caps">Cancel reason</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. client will not pay - cancel and free the slot."
              className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-blood resize-none"
              data-testid="owm-cancel-reason"
            />
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onCancel}
              disabled={!reason.trim()}
              className="inline-flex items-center gap-1.5 rounded-md border border-blood/50 bg-blood/10 px-3 py-1.5 text-xs text-blood hover:bg-blood/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="owm-cancel"
            >
              <Ban size={13} /> Cancel booking
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

function UnpaidArtistBody({
  studioId,
  artistName,
  amountAud,
  dayIso,
  draft,
  setDraft,
  sent,
  onSend,
}: {
  studioId: StudioId;
  artistName: string;
  amountAud: number;
  dayIso: string;
  draft: string;
  setDraft: (v: string) => void;
  sent: boolean;
  onSend: (managerId: string, body: string) => void;
}) {
  const agents = useBrainStore((s) => s.agents);
  const manager = useMemo(
    () =>
      Object.values(agents).find(
        (a) =>
          a.role === 'studio-manager' &&
          (a.primaryStudio === studioId || a.assignedStudios.includes(studioId)),
      ) ?? Object.values(agents).find((a) => a.role === 'studio-manager'),
    [agents, studioId],
  );
  const managerName = manager
    ? `${manager.firstName} ${manager.lastName}`.trim()
    : 'the studio manager';

  const template = useMemo(
    () =>
      `Hi ${manager?.firstName ?? 'there'} - ${artistName} is owed ${aud(amountAud)} for ${studioDisplay(
        studioId,
      )} on ${simDateShort(dayIso)}. Can you sort the payout and confirm back here? Thanks.`,
    [manager, artistName, amountAud, studioId, dayIso],
  );
  const body = draft || template;

  return (
    <>
      <p className="font-serif text-xs text-cream-soft">
        Ops follows up the studio manager, not the artist. This opens a logged, WhatsApp-style
        thread to {managerName} with the amount, artist and date pre-filled.
      </p>
      <label className="block">
        <span className="label-small-caps">Message to {managerName}</span>
        <textarea
          value={body}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          className="mt-2 w-full bg-ink-black/40 border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-gold-bright resize-none"
          data-testid="owm-message-draft"
        />
      </label>
      <div className="flex items-center justify-end gap-2">
        {sent ? (
          <span className="font-mono text-[11px] text-mint" data-testid="owm-message-sent">
            Sent - awaiting reply in the thread.
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => manager && onSend(manager.id, body)}
          disabled={!manager || sent}
          className="inline-flex items-center gap-1.5 rounded-md border border-gold-bright/50 bg-gold-bright/10 px-3 py-1.5 text-xs text-gold-bright hover:bg-gold-bright/20 transition disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="owm-message-manager"
        >
          <MessageSquare size={13} /> Message this person
        </button>
      </div>
    </>
  );
}
